<script lang="ts">
import { useInput, TextualValueType, showRequiredMark, withTextualElInputProps } from '@/forms/input';

const PREFIXES = [
  '+39',
  '+93',
  '+355',
  '+213',
  '+376',
  '+244',
  '+1264',
  '+1268',
  '+599',
  '+966',
  '+54',
  '+374',
  '+297',
  '+247',
  '+61',
  '+43',
  '+994',
  '+1242',
  '+973',
  '+880',
  '+1246',
  '+32',
  '+501',
  '+229',
  '+1441',
  '+975',
  '+375',
  '+591',
  '+387',
  '+267',
  '+55',
  '+673',
  '+359',
  '+226',
  '+257',
  '+855',
  '+237',
  '+1',
  '+238',
  '+235',
  '+56',
  '+86',
  '+357',
  '+57',
  '+269',
  '+242',
  '+850',
  '+82',
  '+225',
  '+506',
  '+385',
  '+53',
  '+45',
  '+1767',
  '+593',
  '+20',
  '+503',
  '+971',
  '+291',
  '+372',
  '+251',
  '+679',
  '+63',
  '+358',
  '+33',
  '+241',
  '+220',
  '+995',
  '+49',
  '+233',
  '+1876',
  '+81',
  '+350',
  '+253',
  '+962',
  '+44',
  '+30',
  '+1473',
  '+299',
  '+590',
  '+1-671',
  '+502',
  '+224',
  '+245',
  '+240',
  '+592',
  '+594',
  '+509',
  '+1-8',
  '+504',
  '+852',
  '+91',
  '+62',
  '+98',
  '+964',
  '+353',
  '+354',
  '+1345',
  '+682',
  '+672',
  '+500',
  '+298',
  '+1-670',
  '+692',
  '+677',
  '+1284',
  '+1340',
  '+972',
  '+7',
  '+254',
  '+996',
  '+686',
  '+965',
  '+856',
  '+266',
  '+371',
  '+961',
  '+231',
  '+218',
  '+423',
  '+370',
  '+352',
  '+853',
  '+389',
  '+261',
  '+265',
  '+960',
  '+60',
  '+223',
  '+356',
  '+212',
  '+596',
  '+222',
  '+230',
  '+52',
  '+691',
  '+373',
  '+976',
  '+382',
  '+1664',
  '+258',
  '+95',
  '+264',
  '+674',
  '+977',
  '+505',
  '+227',
  '+234',
  '+683',
  '+47',
  '+687',
  '+64',
  '+968',
  '+31',
  '+92',
  '+680',
  '+507',
  '+675',
  '+595',
  '+51',
  '+689',
  '+48',
  '+351',
  '+1787',
  '+377',
  '+974',
  '+420',
  '+236',
  '+243',
  '+262',
  '+40',
  '+250',
  '+290',
  '+1869',
  '+508',
  '+1784',
  '+685',
  '+684',
  '+378',
  '+1-7',
  '+239',
  '+221',
  '+381',
  '+248',
  '+232',
  '+65',
  '+963',
  '+421',
  '+386',
  '+252',
  '+34',
  '+94',
  '+27',
  '+249',
  '+597',
  '+46',
  '+41',
  '+268',
  '+992',
  '+886',
  '+66',
  '+255',
  '+670',
  '+228',
  '+690',
  '+676',
  '+1868',
  '+216',
  '+90',
  '+993',
  '+1649',
  '+688',
  '+380',
  '+256',
  '+36',
  '+598',
  '+998',
  '+678',
  '+58',
  '+84',
  '+681',
  '+967',
  '+260',
  '+263',
];

const phonePrefixFromFullPhoneString = (fullPhone: string): string => {
  if (!fullPhone.length) return '+39';
  for (const prefix of PREFIXES) {
    if (fullPhone.startsWith(prefix)) {
      return prefix;
    }
  }
  return '+39';
};

const phoneNumberFromFullPhoneString = (fullPhone: string): string => {
  return fullPhone.replace(phonePrefixFromFullPhoneString(fullPhone), '');
};
</script>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';

const props = defineProps({
  ...withTextualElInputProps(),
});

const prefix = ref(phonePrefixFromFullPhoneString(props.modelValue || ''));
const numberWithoutPrefix = computed(() => phoneNumberFromFullPhoneString(props.modelValue || ''));

watch(
  () => props.modelValue,
  (newValue: TextualValueType) => {
    prefix.value = phonePrefixFromFullPhoneString(newValue || '');
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: TextualValueType): void;
}>();

const emitValue = () => {
  emit('update:modelValue', prefix.value + value.value);
};

const computedValidation = computed(() => ['min:5|regex:^([0-9]+)$', props.validation]);

const { errorMessage, value, setValue, uuid, fieldContext } = useInput(numberWithoutPrefix, computedValidation, props.name, emitValue);

const defaultId = computed(() => props.label.trim().replaceAll(' ', ''));
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="id || `${uuid}-${defaultId}`"
    class="relative"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
  >
    <template #default="{ error }">
      <div class="absolute inset-y-0 left-0 flex items-center">
        <label :for="`${uuid}-prefix`" class="sr-only">Paese</label>
        <select
          :id="id ? `${id}-prefix` : `${uuid}-${defaultId}-prefix`"
          v-model="prefix"
          :name="id ? `${id}-prefix` : `${uuid}-${defaultId}-prefix`"
          :disabled="disabled"
          :class="{ 'text-error  focus:border-0 focus:ring-0': error }"
          autocomplete="country"
          class="focus:ring-outlined-primary text-neutral-inactive focus:__X__border-primary-light h-full rounded-md border-transparent bg-transparent py-0 pr-5 pl-3 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          :data-cy="`${defaultId}-prefix`"
          @change="emitValue"
        >
          <option v-for="pr of PREFIXES" :key="pr">
            {{ pr }}
          </option>
        </select>
      </div>
      <input
        :id="id || `${uuid}-${defaultId}`"
        :value="numberWithoutPrefix"
        :name="props.name || id || `${uuid}-${defaultId}`"
        :disabled="disabled"
        type="text"
        :class="{
          error: error,
        }"
        class="el-input !pl-[86px]"
        autocomplete="off"
        :placeholder="placeholder"
        :data-cy="`${defaultId}-input`"
        @input="e => setValue((e.target as HTMLInputElement).value)"
        @blur="fieldContext.validate()"
      />
    </template>
  </ElInputContainer>
</template>
